import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api/client";
import { facilitiesApi } from "./api";

/**
 * Empty-state CTA shown on /dashboard and /facilities when the user
 * has no facilities yet. Two paths:
 *   - Create one of your own → routes to /facilities (where the
 *     create form lives).
 *   - Try the demo → POST /api/facilities/seed-demo/ + jump straight
 *     into the boat-club hub.
 */
export function OnboardingHero({
  primaryHref = "/facilities",
}: {
  primaryHref?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const seed = useMutation({
    mutationFn: () => facilitiesApi.seedDemo(),
    onSuccess: (facility) => {
      qc.invalidateQueries({ queryKey: ["facilities"] });
      navigate(`/facilities/${facility.id}`);
    },
    onError: (err: unknown) => {
      const detail =
        err instanceof ApiError
          ? ((err.data as { detail?: string } | null)?.detail ?? null)
          : null;
      setError(detail ?? t("onboarding.demo_error"));
    },
  });

  return (
    <section className="rounded-lg border border-brand-500/30 bg-gradient-to-br from-brand-50 to-white p-6 sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
        {t("onboarding.kicker")}
      </p>
      <h2 className="mt-1 text-xl font-semibold tracking-tight sm:text-2xl">
        {t("onboarding.headline")}
      </h2>
      <p className="mt-2 max-w-prose text-sm text-slate-600">
        {t("onboarding.blurb")}
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button size="lg" onClick={() => navigate(primaryHref)}>
          {t("onboarding.cta_create")}
        </Button>
        <Button
          size="lg"
          variant="secondary"
          disabled={seed.isPending}
          onClick={() => {
            setError(null);
            seed.mutate();
          }}
        >
          {seed.isPending
            ? t("onboarding.cta_demo_loading")
            : t("onboarding.cta_demo")}
        </Button>
      </div>

      <p className="mt-3 text-xs text-slate-500">
        {t("onboarding.demo_blurb")}
      </p>
      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
    </section>
  );
}
